/**
 * @fileoverview Stepper component for displaying a progress stepper in the Storyteller web application.
 * This component provides a mobile and desktop version of the stepper, with different layouts and functionality.
 *
 * @example
 * // Basic usage
 * <Stepper assessments={assessments} shareConfig={shareConfig} onShare={onShare} />
 */

import { useTranslation } from 'react-i18next'
import CheckIcon from './assets/check.svg'
import { useResize } from './hooks/useResize'

import css from './Stepper.module.css'

/**
 * @interface StepperProps
 * @description Props for the Stepper component.
 * @property {Novoic.Assessment[]} assessments - The assessments to display in the stepper.
 * @property {Object} shareConfig - The configuration object for the share modal.
 * @property {string} shareConfig.invitation_url - The URL to be shared.
 * @property {Function} onShare - A function to handle the share action.
 */
interface StepperProps {
    assessments: Novoic.Assessment[]
    shareConfig?: { invitation_url: string }
    onShare?: () => void
}

/**
 * @function Stepper
 * @description Component for displaying a progress stepper in the Storyteller web application.
 * @param {StepperProps} props - The props for the Stepper component.
 * @returns {JSX.Element} The Stepper component.
 */
export function Stepper({ assessments, shareConfig, onShare }: StepperProps) {
    const screenWidth = useResize()
    const isMobile = screenWidth < 768

    return isMobile ? (
        <MobileStepper assessments={assessments} shareConfig={shareConfig} onShare={onShare} />
    ) : (
        <DesktopStepper assessments={assessments} />
    )
}

/**
 * @function MobileStepper
 * @description Component for displaying a progress stepper in the Storyteller web application on mobile devices.
 * @param {StepperProps} props - The props for the MobileStepper component.
 * @returns {JSX.Element} The MobileStepper component.
 */
function MobileStepper({ assessments, shareConfig, onShare = () => undefined }: StepperProps) {
    const { t } = useTranslation()

    const current = assessments.find((assessment) => assessment.current)
    const currentIndex = assessments.indexOf(current)

    if (!current?.annotation || assessments.length === 0) {
        return null
    }

    return (
        <div className={css['progress-container']}>
            <section className={css.container}>
                <section className={css.details}>
                    <span className={css.title}>
                        {t('stepper.title_mobile', {
                            count: currentIndex + 1,
                            total: assessments.length,
                            title: t(current.annotation.displayName + '_mobile'),
                        })}
                    </span>
                    <span className={css.time}>
                        {t('breadcrumbs.estimatedTime', {
                            count: current.annotation?.estimatedTime as number,
                        })}
                    </span>
                </section>
                <section>
                    {shareConfig && (
                        <button className={`button confirm ${css.cta}`} onClick={onShare}>
                            {t('share.cta')}
                        </button>
                    )}
                </section>
            </section>
            <section className={css.progress}>
                {assessments.map((assessment, index) => {
                    return (
                        <div
                            key={assessment.name}
                            className={`${css['progress-item-mobile']} ${
                                index === currentIndex ? css.active : ''
                            } ${index < currentIndex ? css.completed : ''}`}
                        ></div>
                    )
                })}
            </section>
        </div>
    )
}

/**
 * @function DesktopStepper
 * @description Component for displaying a progress stepper in the Storyteller web application on desktop devices.
 * @param {StepperProps} props - The props for the DesktopStepper component.
 * @returns {JSX.Element} The DesktopStepper component.
 */
function DesktopStepper({ assessments }: StepperProps) {
    const { t } = useTranslation()

    const current = assessments.find((assessment) => assessment.current)
    const currentIndex = assessments.indexOf(current)

    if (!current?.annotation || assessments.length === 0) {
        return null
    }

    return (
        <div className={css['progress-container']}>
            <section className={css.progress}>
                {assessments.map((assessment, index) => {
                    return (
                        <div
                            className={`${css['progress-item']} ${
                                index === currentIndex ? css.active : ''
                            } ${index < currentIndex ? css.completed : ''}`}
                            key={assessment.name}
                        >
                            <div className={css.num}>
                                {index < currentIndex ? <CheckIcon /> : <span>{index + 1}</span>}
                            </div>
                            <span className={css.title}>
                                {t(assessment.annotation?.displayName as string)}
                            </span>
                            <span className={css.time}>
                                {index < currentIndex
                                    ? t('breadcrumbs.done')
                                    : t('breadcrumbs.estimatedTime', {
                                          count: assessment.annotation?.estimatedTime as number,
                                      })}
                            </span>
                        </div>
                    )
                })}
            </section>
        </div>
    )
}
